import { BaseInputProps } from '@components/InputFieldControlled/InputText.types';
import {
    Autocomplete,
    AutocompleteInputChangeReason,
    Box,
    CircularProgress,
    FormGroup,
    FormLabel,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { composeRules2, getValidationRules } from '@utils/composeRules';
import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { City } from '@components/InputCity/types';
import { searchArea } from '@components/InputCity/hereApi';

type Props = {
    name?: string;
    label?: string;
    control: any;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    defaultValue?: string;
    onSelect?: (city: string, postal: string) => void;
};

type PostalOption = City & { label: string; value: string };

const InputPostal = React.forwardRef((props: BaseInputProps & Props, ref) => {
    const theme = useTheme();
    const rules = composeRules2(props);
    const [options, setOptions] = useState<PostalOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [inputValue, setInputValue] = useState('');

    const handleInputChange = (
        _event: React.SyntheticEvent,
        value: string,
        reason: AutocompleteInputChangeReason,
    ) => {
        setInputValue(value);
        if (reason !== 'input') {
            return;
        }
        if (!value) {
            setOptions([]);
            return;
        }
        setLoading(true);
        searchArea(value, results => {
            setOptions((results ?? []) as PostalOption[]);
            setLoading(false);
        });
    };

    return (
        <Controller
            control={props.control}
            name={props.name}
            defaultValue={props.defaultValue ?? null}
            rules={{
                validate: getValidationRules(rules ?? {}),
                required: props.required,
            }}
            render={({ field: { ref: fieldRef, value, onChange }, fieldState: { error } }) => {
                const selectedOption =
                    options.find(o => o.address?.postalCode === value) ??
                    (value
                        ? ({
                              value,
                              label: value,
                              address: { city: value, postalCode: value } as any,
                          } as PostalOption)
                        : null);

                return (
                    <FormGroup sx={{ width: '100%' }}>
                        {props.label && (
                            <FormLabel
                                sx={{
                                    color: error
                                        ? theme.palette.errorRed.main
                                        : props.disabled
                                          ? theme.palette.text.disabled
                                          : theme.palette.text.primary,
                                }}
                            >
                                {props.label}
                            </FormLabel>
                        )}

                        {props.viewOnly ? (
                            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                                {value ?? '-'}
                            </Typography>
                        ) : (
                            <Autocomplete<PostalOption>
                                ref={fieldRef}
                                options={options}
                                value={selectedOption}
                                inputValue={inputValue}
                                loading={loading}
                                disabled={props.disabled}
                                onInputChange={handleInputChange}
                                onChange={(_event, selected: PostalOption | null) => {
                                    setInputValue(selected?.address?.postalCode ?? '');
                                    onChange(selected?.address?.postalCode ?? null);
                                    props?.onSelect?.(
                                        selected?.address?.city ?? '',
                                        selected?.address?.postalCode ?? '',
                                    );
                                }}
                                getOptionLabel={option => option.address?.postalCode ?? ''}
                                isOptionEqualToValue={(option, val) =>
                                    option.address?.postalCode === val?.address?.postalCode
                                }
                                noOptionsText="Tapez pour rechercher le code postal"
                                loadingText="Recherche en cours..."
                                filterOptions={x => x}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        placeholder={props.placeholder ?? props.label}
                                        error={!!error}
                                        helperText={error?.message}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading && <CircularProgress size={16} />}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                                renderOption={(optionProps, option) => (
                                    <li {...optionProps} key={option.locationId ?? option.address?.postalCode}>
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {option.address?.city}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                {option.address?.postalCode}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                            />
                        )}
                    </FormGroup>
                );
            }}
        />
    );
});

InputPostal.displayName = 'InputPostal';

export default InputPostal;
